/**
 * Board renderer — syncs PixiJS display with GameState board.
 * Positions gear-bubble sprites at hex grid coordinates and applies color tints.
 * Each gear gets a subtle unique rotation for visual variety.
 */

import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import type { GameState, Bubble } from '../game/types.js';
import { hexToPixel, coordToKey } from '../game/grid/hex.js';
import { CONFIG } from '../game/config.js';
import { BUBBLE_COLORS, WILD_COLOR, OBSTACLE_COLOR, getColorSymbol } from './colors.js';
import type { SpritePool } from './sprite-pool.js';
import { applyLowMotionPulse } from './low-motion.js';

/**
 * Frame-to-frame previous alpha cache for the landed-keg danger glow.
 * Module-scoped (not per-keg) because there's at most one bomb keg
 * landed-and-pulsing on the playfield at a time, and `clampLuminanceDelta`
 * only needs the prior target to bound the next one. When low-motion is
 * off, this is unused (the clamp short-circuits).
 */
let prevLandedKegPulseAlpha = 0;

/** Tracks which sprites are assigned to which board positions */
interface BoardSpriteMap {
  sprites: Map<string, Sprite>;
  symbols: Map<string, Text>;
  indicators: Map<string, Graphics>;
}

const SYMBOL_STYLE = new TextStyle({
  fontFamily: '"Courier New", monospace',
  fontSize: 10,
  fill: 0x1a1510, // dark soot background for engraved look
  align: 'center',
});

/**
 * Create a board sprite map for tracking assigned sprites.
 */
export function createBoardSpriteMap(): BoardSpriteMap {
  return {
    sprites: new Map(),
    symbols: new Map(),
    indicators: new Map(),
  };
}

/**
 * Render the board state — sync sprites with the current bubble positions.
 * Uses sprite pool for efficient reuse.
 */
export function renderBoard(
  container: Container,
  state: GameState,
  pool: SpritePool,
  map: BoardSpriteMap,
): void {
  const board = state.board;
  const cellSize = CONFIG.CELL_SIZE;

  // Track which keys are still present
  const currentKeys = new Set<string>();

  // Add/update sprites for each bubble
  for (const [key, bubble] of board.bubbles) {
    currentKeys.add(key);

    let sprite = map.sprites.get(key);
    if (!sprite) {
      // New bubble — acquire from pool
      const acquired = pool.acquire();
      if (!acquired) continue; // Pool exhausted
      sprite = acquired;
      map.sprites.set(key, sprite);

      // Give each gear a unique rotation based on its grid position
      const hash = hashKey(key);
      sprite.rotation = (hash % 628) / 100; // 0 to ~2pi
    }

    // Position at hex coordinates
    const pixel = hexToPixel(bubble.coord, cellSize);
    sprite.position.set(pixel.x, pixel.y);
    sprite.scale.set(cellSize / (sprite.texture.width / 2));

    // Apply color
    applyBubbleAppearance(sprite, bubble, container, map, key);
  }

  // Release sprites for removed bubbles
  for (const [key, sprite] of map.sprites) {
    if (!currentKeys.has(key)) {
      pool.release(sprite);
      map.sprites.delete(key);

      // Clean up symbol text
      const symbol = map.symbols.get(key);
      if (symbol) {
        symbol.destroy();
        map.symbols.delete(key);
      }

      // Clean up indicator graphics
      const indicator = map.indicators.get(key);
      if (indicator) {
        indicator.destroy();
        map.indicators.delete(key);
      }
    }
  }
}

/** Simple hash from coordinate key for deterministic gear rotation */
function hashKey(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * The rotation the board renderer would assign to the gear at `key`.
 * Cascade held-gear effects call this to reproduce the exact rotation the
 * board sprite had — otherwise the stand-in visibly snaps to rotation 0
 * the frame the board sprite is released.
 */
export function bubbleGearRotation(key: string): number {
  return (hashKey(key) % 628) / 100;
}

/**
 * Apply the visual appearance (tint + indicators) for a bubble sprite.
 */
function applyBubbleAppearance(
  sprite: Sprite,
  bubble: Bubble,
  container: Container,
  map: BoardSpriteMap,
  key: string,
): void {
  sprite.alpha = 1;

  if (bubble.isObstacle) {
    sprite.tint = OBSTACLE_COLOR;
    ensureIndicator(container, map, key, sprite, 'obstacle');
    return;
  }

  if (bubble.isBomb) {
    // The bomb only sits on the board during the ~100ms landed-hold before
    // the blast resolves. Draw the full powder keg over the gear slot so it
    // keeps its flight silhouette — tinting the gear red instead caused a
    // visible "red gear flash" right before the explosion.
    sprite.alpha = 0;
    ensureIndicator(container, map, key, sprite, 'bomb');
    return;
  }

  if (bubble.isWild) {
    sprite.tint = WILD_COLOR;
    ensureIndicator(container, map, key, sprite, 'wild');
    return;
  }

  // Normal bubble
  const colorInfo = BUBBLE_COLORS[bubble.color];
  sprite.tint = colorInfo.hex;

  // Colorblind symbol (engraved rune)
  ensureSymbol(container, map, key, sprite, getColorSymbol(bubble.color));
}

/**
 * Ensure a colorblind-friendly text symbol exists on the sprite.
 */
function ensureSymbol(
  container: Container,
  map: BoardSpriteMap,
  key: string,
  sprite: Sprite,
  symbol: string,
): void {
  let text = map.symbols.get(key);
  if (!text) {
    text = new Text({ text: symbol, style: SYMBOL_STYLE });
    text.anchor.set(0.5);
    container.addChild(text);
    map.symbols.set(key, text);
  } else {
    text.text = symbol;
  }
  text.position.set(sprite.x, sprite.y);
}

/**
 * Ensure a visual indicator (wild/bomb/obstacle) exists on the sprite.
 */
function ensureIndicator(
  container: Container,
  map: BoardSpriteMap,
  key: string,
  sprite: Sprite,
  type: 'wild' | 'bomb' | 'obstacle',
): void {
  let gfx = map.indicators.get(key);
  if (!gfx) {
    gfx = new Graphics();
    container.addChild(gfx);
    map.indicators.set(key, gfx);
  }

  gfx.clear();
  gfx.position.set(sprite.x, sprite.y);

  const r = CONFIG.CELL_SIZE * 0.6;

  switch (type) {
    case 'wild':
      // Crystal artifact glow ring
      gfx.circle(0, 0, r);
      gfx.stroke({ color: 0xc49a3c, width: 2 }); // brass ring
      break;
    case 'bomb':
      drawLandedKeg(gfx, r);
      break;
    case 'obstacle':
      // Rivet marks on iron plate
      gfx.circle(-r * 0.3, -r * 0.3, 1.5);
      gfx.circle(r * 0.3, -r * 0.3, 1.5);
      gfx.circle(-r * 0.3, r * 0.3, 1.5);
      gfx.circle(r * 0.3, r * 0.3, 1.5);
      gfx.fill({ color: 0x888899 });
      break;
  }
}

/**
 * Draw a powder keg at the origin — matches the in-flight projectile silhouette
 * so a bomb that lands reads as the same object it was while flying.
 */
function drawLandedKeg(gfx: Graphics, r: number): void {
  const t = Date.now();
  const pulse = 0.6 + Math.sin(t * 0.015) * 0.4;

  // Danger glow (dominates before the blast lands). T-6.7c: the per-frame
  // alpha is gated through `applyLowMotionPulse` so when the player has
  // Reduce Motion on the frame-to-frame luminance delta stays under the
  // W3C 2.3.3 10% photosensitive-epilepsy threshold. When low-motion is
  // off the helper short-circuits and this draws the unchanged
  // `0.3 * pulse` alpha so existing goldens still pass.
  const targetAlpha = 0.3 * pulse;
  const safeAlpha = applyLowMotionPulse(prevLandedKegPulseAlpha, targetAlpha);
  prevLandedKegPulseAlpha = safeAlpha;

  gfx.circle(0, 0, r + 3);
  gfx.fill({ color: 0xc4453a, alpha: safeAlpha });

  // Keg body — opaque so the gear sprite underneath doesn't bleed through
  gfx.circle(0, 0, r);
  gfx.fill({ color: 0x2a1d14 });
  gfx.circle(0, 0, r);
  gfx.stroke({ color: 0x0a0806, width: 2 });

  // Brass band
  gfx.rect(-r * 0.7, -r * 0.15, r * 1.4, r * 0.3);
  gfx.fill({ color: 0xc49a3c });
  gfx.rect(-r * 0.7, -r * 0.15, r * 1.4, r * 0.3);
  gfx.stroke({ color: 0x8b6e28, width: 1 });

  // Fuse stub + sputtering spark
  gfx.moveTo(0, -r);
  gfx.lineTo(0, -r * 1.35);
  gfx.stroke({ color: 0x5c3a1e, width: 2 });
  const flicker = 0.7 + Math.sin(t * 0.06) * 0.3 + Math.sin(t * 0.11) * 0.15;
  gfx.circle(0, -r * 1.35, 2.8 * flicker);
  gfx.fill({ color: 0xffe8c0, alpha: 0.95 });
  gfx.circle(0, -r * 1.35, 1.6 * flicker);
  gfx.fill({ color: 0xffffff });
}

/**
 * Clean up all board sprites and return them to the pool.
 */
export function clearBoard(pool: SpritePool, map: BoardSpriteMap): void {
  for (const [, sprite] of map.sprites) {
    pool.release(sprite);
  }
  map.sprites.clear();

  for (const [, text] of map.symbols) {
    text.destroy();
  }
  map.symbols.clear();

  for (const [, gfx] of map.indicators) {
    gfx.destroy();
  }
  map.indicators.clear();
}
